import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks';
import EditorManager from 'Editor/services/EditorManager';
import ToolbarDropdown from 'Editor/components/ToolbarDropdown/ToolbarDropdown';
import { completeAction, resetAction } from 'App/redux/onboardingSlice';

type FontFamilyProps = {
  isReadOnlyMode: boolean;
};

const FontFamily = ({ isReadOnlyMode }: FontFamilyProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const fontFamily = useSelector((state) => state.editor.toolbar.fontFamily);
  const externalFonts = useSelector((state) => state.fonts.external);
  const defaultFonts = useSelector((state) => state.fonts.default);

  const parseOptions = (options: typeof defaultFonts) => {
    return options.map((option) => {
      let description = option.description?.toString();

      if (!option.supported) {
        const newOption = {
          ...option,
          description,
          suffix: {
            icon: { icon: 'regular-font-circle-question' as const, iconPrefix: 'fak' as const },
          },
        };
        newOption.fontFamily = option.label as string;
        return newOption;
      }

      return {
        ...option,
        description,
        fontFamily: option.label as string,
        id: option.value === 'times new roman' ? 'fontFamilyTargetAction' : undefined,
      };
    });
  };

  const options = useMemo(() => {
    return [
      {
        label: intl.formatMessage({ id: 'EXTERNAL_FONTS' }),
        options: parseOptions(externalFonts),
        testId: 'external-fonts',
      },
      {
        label: intl.formatMessage({ id: 'DEFAULT_FONTS' }),
        options: parseOptions(defaultFonts),
        testId: 'default-fonts',
      },
    ];
  }, [externalFonts, defaultFonts]);

  return (
    <ToolbarDropdown
      disabled={isReadOnlyMode}
      options={options}
      onChange={(option) => {
        if (option) {
          EditorManager.getInstance().toggleSelectionStyle('fontFamily', option.label);

          dispatch(completeAction('editor_writing_font_select'));
        }
      }}
      value={
        fontFamily
          ? options[0].options.find((font) => font.value === fontFamily.toLowerCase()) ||
            options[1].options.find((font) => font.value === fontFamily.toLowerCase())
          : undefined
      }
      width="15"
      isCreatable
      testId="font-family"
      id="fontFamilySelect"
      menuWidth="30rem"
      onMenuOpen={() => dispatch(completeAction('editor_writing_font_open'))}
      onMenuClose={() => dispatch(resetAction('editor_writing_font_open'))}
    />
  );
};

export default FontFamily;
